import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import type { NotificationPreferences } from '@indutrust/notification-model';
import { resolveHttpError } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withState } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { concatMap, switchMap } from 'rxjs';
import { NotificationPreferencesApiClient } from '../notification-preferences-api-client.service';
import { notificationPreferencesEvents } from './notification-preferences.events';

type NotificationPreferencesState = {
  error: string | null;
  isLoading: boolean;
  isSaving: boolean;
  preferences: NotificationPreferences | null;
  previousPreferences: NotificationPreferences | null;
};

const initialState: NotificationPreferencesState = {
  error: null,
  isLoading: false,
  isSaving: false,
  preferences: null,
  previousPreferences: null,
};

function mergePreferences(
  current: NotificationPreferences,
  patch: Partial<NotificationPreferences>
): NotificationPreferences {
  return {
    ...current,
    ...patch,
    eventTypes: {
      ...current.eventTypes,
      ...patch.eventTypes,
    },
  };
}

/**
 * Pattern B store for the `/notifications/preferences` page.
 *
 * Loads the current-user preferences singleton and applies optimistic
 * PATCH updates with rollback on failure.
 */
export const NotificationPreferencesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withReducer(
    on(notificationPreferencesEvents.loadRequested, () => ({
      error: null,
      isLoading: true,
    })),
    on(notificationPreferencesEvents.loadSucceeded, ({ payload }) => ({
      isLoading: false,
      preferences: payload.preferences,
      previousPreferences: null,
    })),
    on(notificationPreferencesEvents.loadFailed, ({ payload }) => ({
      error: payload.error,
      isLoading: false,
    })),
    on(notificationPreferencesEvents.updateRequested, ({ payload }, state) => {
      const current = state.preferences;
      if (!current) {
        return { isSaving: true };
      }
      return {
        error: null,
        isSaving: true,
        preferences: mergePreferences(current, payload.patch),
        previousPreferences: current,
      };
    }),
    on(notificationPreferencesEvents.updateSucceeded, ({ payload }) => ({
      isSaving: false,
      preferences: payload.preferences,
      previousPreferences: null,
    })),
    on(notificationPreferencesEvents.updateFailed, ({ payload }, state) => ({
      error: payload.error,
      isSaving: false,
      preferences: state.previousPreferences ?? state.preferences,
      previousPreferences: null,
    }))
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      api = inject(NotificationPreferencesApiClient)
    ) => ({
      load$: events.on(notificationPreferencesEvents.loadRequested).pipe(
        switchMap(() =>
          api.get().pipe(
            mapResponse({
              next: (preferences) =>
                notificationPreferencesEvents.loadSucceeded({ preferences }),
              error: (error: HttpErrorResponse) =>
                notificationPreferencesEvents.loadFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
      // concatMap so rapid toggles do not cancel in-flight PATCHes (partial
      // bodies would otherwise drop earlier field updates on the server).
      update$: events.on(notificationPreferencesEvents.updateRequested).pipe(
        concatMap(({ payload }) =>
          api.updateCurrent(payload.patch).pipe(
            mapResponse({
              next: (preferences) =>
                notificationPreferencesEvents.updateSucceeded({ preferences }),
              error: (error: HttpErrorResponse) =>
                notificationPreferencesEvents.updateFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
    })
  )
);
